
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AiMarketAnalysisService, AiAnalysisSession } from '@/services/AiMarketAnalysisService';
import { useAuth } from '@/contexts/AuthContext';

interface UseAnalysisHistoryOptions {
  limit?: number;
  symbol?: string;
  status?: string;
}

export const useAnalysisHistory = (options: UseAnalysisHistoryOptions = {}) => {
  const { user } = useAuth();
  const { limit = 20, symbol, status } = options;

  const {
    data: sessions = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['analysis-history', user?.id, limit, symbol, status],
    queryFn: async () => {
      if (!user?.id) return [];
      
      let allSessions = await AiMarketAnalysisService.getUserAnalysisSessions(user.id, limit);
      
      // Apply filters
      if (symbol) {
        allSessions = allSessions.filter(session => 
          session.symbol.toLowerCase().includes(symbol.toLowerCase())
        );
      }
      
      // Only filter by status if it's not empty (meaning "all" was selected)
      if (status && status.trim() !== '') {
        allSessions = allSessions.filter(session => session.status === status);
      }
      
      return allSessions;
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refetch every 30 seconds to catch updates
  });

  const deleteSession = async (sessionId: string) => {
    const result = await AiMarketAnalysisService.deleteAnalysisSession(sessionId);
    if (result.success) {
      refetch();
    }
    return result;
  };

  return {
    sessions,
    isLoading,
    error,
    refetch,
    deleteSession
  };
};

export const useAnalysisDetail = (sessionId: string) => {
  const {
    data: session,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['analysis-detail', sessionId],
    queryFn: () => AiMarketAnalysisService.getAnalysisSession(sessionId),
    enabled: !!sessionId,
  });

  return {
    session,
    isLoading,
    error,
    refetch
  };
};
